import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  ErrorState,
  FormStack,
  Input,
  Page,
  Switch,
  Text,
} from "@marekl/adminer/components";
import {
  CodeEditor,
  type CodeEditorHandle,
  type CodeEditorLanguage,
} from "@marekl/adminer/code-editor";
import { useT } from "@marekl/adminer/hooks";
import { apiErrorMessage, notifySuccess } from "@marekl/adminer/api";
import { UnsavedChangesModal, useUnsavedChangesGuard } from "@marekl/adminer/shell";
import { AppTopbar } from "../../../shell/AppTopbar";
import { AudienceFields, useAudienceLabel } from "../../../components/AudienceFields";
import { CustomPageContent } from "../../../components/CustomPageContent";
import {
  useCustomPageForEdit,
  useSaveCustomPage,
  type CustomPage,
  type CustomPagePayload,
} from "../../../lib/queries/customPages";
import { customPagePath, slugify } from "../../../lib/customPages";
import styles from "./CustomPageEditPage.module.css";

const EMPTY: CustomPagePayload = {
  title: "",
  slug: "",
  body: "",
  published: false,
  sort_order: 0,
  audit_views: false,
  roles: [],
  team_ids: [],
  federation_ids: [],
};

function toPayload(p: CustomPage): CustomPagePayload {
  return {
    title: p.title,
    slug: p.slug,
    body: p.body,
    published: p.published,
    sort_order: p.sort_order,
    audit_views: p.audit_views,
    roles: p.roles,
    team_ids: p.team_ids,
    federation_ids: p.federation_ids,
  };
}

function samePayload(a: CustomPagePayload, b: CustomPagePayload): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

type Tab = "edit" | "preview";

// Markdown highlights the HTML inside it too. Module scope: a loader made per
// render would reload the language.
const markdown: CodeEditorLanguage = () =>
  import("@codemirror/lang-markdown").then((m) => m.markdown());

// What „Vložit" puts at the cursor: the tags CustomPageContent turns into
// library components, with their attributes written out, plus the Markdown
// an author reaches for most. Keep the tags in step with its switch.
const SNIPPETS: { label: string; text: string }[] = [
  { label: "Karta", text: '<cmf-card title="Nadpis karty">\n\nText karty.\n\n</cmf-card>\n' },
  {
    label: "Mřížka",
    text:
      '<cmf-grid columns="2">\n<cmf-card title="První">\n\nText.\n\n</cmf-card>\n' +
      '<cmf-card title="Druhá">\n\nText.\n\n</cmf-card>\n</cmf-grid>\n',
  },
  {
    label: "Upozornění",
    text: '<cmf-alert variant="info" title="Upozornění">\nText upozornění.\n</cmf-alert>\n',
  },
  { label: "Nadpis", text: "\n## Nadpis\n" },
  { label: "Odkaz", text: "[text odkazu](https://)" },
];

/** Creates a stránka (`/admin/stranky/nova`) or edits one (`/admin/stranky/:id`). */
export function CustomPageEditPage() {
  const { id } = useParams<{ id: string }>();
  // Both routes render this same element, and react-router keeps it mounted
  // across them. Keyed by id, every page gets a fresh editor: the redirect
  // after „Vytvořit" stops the address following the title, and moving
  // between two pages can never save one's draft into the other.
  return <Editor key={id ?? "new"} id={id} />;
}

function Editor({ id }: { id: string | undefined }) {
  const navigate = useNavigate();
  const t = useT();
  const existing = useCustomPageForEdit(id);
  const save = useSaveCustomPage(id ?? null);
  const audienceLabel = useAudienceLabel();
  const editor = useRef<CodeEditorHandle>(null);

  const [stored, setStored] = useState<CustomPagePayload | null>(id ? null : EMPTY);
  const [draft, setDraft] = useState<CustomPagePayload | null>(id ? null : EMPTY);
  // A new page's address follows its title until somebody types one.
  const [slugTouched, setSlugTouched] = useState(!!id);
  const [tab, setTab] = useState<Tab>("edit");
  // Where to go once the saved draft reads clean (AGENTS.md: never in the
  // same tick as the state update, or the guard blocks its own redirect).
  const [goTo, setGoTo] = useState<string | null>(null);

  useEffect(() => {
    if (!existing.data || stored) return;
    const p = toPayload(existing.data);
    setStored(p);
    setDraft(p);
  }, [existing.data, stored]);

  const dirty = !!draft && !!stored && !samePayload(draft, stored);
  const guard = useUnsavedChangesGuard(dirty);

  useEffect(() => {
    if (goTo && !dirty) {
      navigate(goTo, { replace: true });
      setGoTo(null);
    }
  }, [goTo, dirty, navigate]);

  const crumbs = [{ label: "Stránky", href: "/admin/stranky" }, { label: id ? "Upravit" : "Nová" }];

  if (existing.error) {
    return (
      <>
        <AppTopbar crumbs={crumbs} />
        <Page>
          <Page.Content>
            <ErrorState error={existing.error} onRetry={existing.refetch} />
          </Page.Content>
        </Page>
      </>
    );
  }

  const set = (patch: Partial<CustomPagePayload>) => draft && setDraft({ ...draft, ...patch });
  const canSave = !!draft && draft.title.trim() !== "" && draft.slug !== "" && (!id || dirty);

  async function onSave() {
    if (!draft) return;
    let saved: CustomPage;
    try {
      saved = await save.mutateAsync(draft);
    } catch {
      return; // rendered under the header
    }
    notifySuccess(id ? "Stránka je uložena." : "Stránka je vytvořena.");
    const p = toPayload(saved);
    setStored(p);
    setDraft(p);
    if (!id) setGoTo(`/admin/stranky/${saved.id}`);
  }

  const title = id ? (stored?.title ?? "Stránka") : "Nová stránka";

  return (
    <>
      <AppTopbar crumbs={crumbs} />
      <Page>
        <Page.Header
          title={
            <>
              {title}
              {/* A page not created yet has no status to report. */}
              {id &&
                stored &&
                (stored.published ? (
                  <Badge variant="ok">Zveřejněno</Badge>
                ) : (
                  <Badge variant="muted">Nezveřejněno</Badge>
                ))}
            </>
          }
          subtitle={stored?.slug ? customPagePath(stored.slug) : undefined}
          actions={
            <>
              {id && stored && (
                <Button onClick={() => navigate(customPagePath(stored.slug))}>Zobrazit stránku</Button>
              )}
              <Button variant="primary" disabled={!canSave} loading={save.isPending} onClick={onSave}>
                {id ? "Uložit" : "Vytvořit"}
              </Button>
            </>
          }
        />

        <Page.Content stack gap="lg">
          {save.error && <Alert variant="err">{apiErrorMessage(save.error, t)}</Alert>}
          <Card>
            <Card.Header title="Obsah" />
            <Card.Tabs
              tabs={[
                { id: "edit", label: "Upravit" },
                { id: "preview", label: "Náhled" },
              ]}
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              aria-label="Obsah stránky"
            />
            {tab === "edit" && (
              <Card.Toolbar>
                <div className={styles.insert} role="group" aria-label="Vložit do obsahu">
                  <Text variant="muted">Vložit</Text>
                  {SNIPPETS.map((s) => (
                    <Button
                      key={s.label}
                      size="sm"
                      weight="outline"
                      disabled={!draft}
                      onClick={() => editor.current?.insert(s.text)}
                    >
                      {s.label}
                    </Button>
                  ))}
                </div>
              </Card.Toolbar>
            )}
            <Card.Content isLoading={!draft}>
              {draft && (
                <>
                  {/* Hidden rather than unmounted while the preview shows:
                      the editor keeps its undo history, cursor and scroll. */}
                  <div hidden={tab !== "edit"}>
                    <CodeEditor
                      ref={editor}
                      label="Obsah"
                      language={markdown}
                      height="32rem"
                      value={draft.body}
                      onChange={(body) => set({ body })}
                      hint="Markdown; lze vložit i HTML včetně <iframe>, <style> a <script>. Obsah se nijak nekontroluje, vložený kód se spustí každému, kdo stránku otevře."
                    />
                  </div>
                  {tab === "preview" && (
                    <div className={styles.previewStack}>
                      <Text variant="muted">Skripty se v náhledu nespouštějí, jen na stránce samotné.</Text>
                      <div className={styles.preview}>
                        <CustomPageContent body={draft.body} runScripts={false} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Content>
          </Card>
        </Page.Content>

        <Page.Aside aria-label="Nastavení stránky">
          <Card>
            <Card.Header title="Stránka" />
            <Card.Content isLoading={!draft}>
              {draft && (
                <FormStack>
                  <Input
                    label="Název"
                    required
                    maxLength={191}
                    value={draft.title}
                    onChange={(e) =>
                      set({
                        title: e.target.value,
                        ...(slugTouched ? {} : { slug: slugify(e.target.value) }),
                      })
                    }
                    hint="Nadpis stránky a položka v menu."
                  />
                  <Input
                    label="Adresa"
                    required
                    maxLength={100}
                    prefix="/stranky/"
                    value={draft.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      set({ slug: e.target.value });
                    }}
                    hint="Malá písmena bez diakritiky, číslice a pomlčky."
                  />
                </FormStack>
              )}
            </Card.Content>
          </Card>

          <Card>
            <Card.Header title="Zveřejnění" />
            <Card.Content isLoading={!draft}>
              {draft && (
                <FormStack>
                  <Switch
                    label="Zveřejněno"
                    checked={draft.published}
                    onChange={(e) => set({ published: e.target.checked })}
                    hint="Nezveřejněnou stránku vidí jen správci stránek a administrátoři."
                  />
                  <Input
                    label="Pořadí v menu"
                    type="number"
                    value={String(draft.sort_order)}
                    onChange={(e) => set({ sort_order: Number(e.target.value) || 0 })}
                    hint="Stránky v menu se řadí podle čísla, při shodě podle názvu."
                  />
                  <Switch
                    label="Zaznamenávat zobrazení"
                    checked={draft.audit_views}
                    onChange={(e) => set({ audit_views: e.target.checked })}
                    hint="Každé otevření stránky se zapíše do audit logu: kdo a kdy. Čtenáři se to na stránce oznámí."
                  />
                </FormStack>
              )}
            </Card.Content>
          </Card>

          <Card>
            <Card.Header title="Komu" />
            <Card.Content isLoading={!draft}>
              {draft && (
                <FormStack>
                  <AudienceFields
                    value={draft}
                    onChange={(targets) => set(targets)}
                    hint="Role a zároveň tým nebo federace. Globální role (např. Administrátor) patří do všech. Prázdné pole znamená všechny přihlášené."
                  />
                  <Alert variant="info">
                    {`Uvidí: ${audienceLabel(draft) || "všichni přihlášení"}`}
                  </Alert>
                </FormStack>
              )}
            </Card.Content>
          </Card>
        </Page.Aside>
      </Page>

      <UnsavedChangesModal
        open={guard.blocked}
        description="Stránka zatím není uložená. Opustit ji a zahodit změny?"
        onClose={guard.cancel}
        onConfirm={guard.proceed}
      />
    </>
  );
}
